import { useState } from "react";
import { motion } from "framer-motion";
import { Candy, Gift, Percent, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";

const PERKS = [
  { icon: Candy, color: "text-cosmic-cyan", bg: "from-cosmic-cyan/20 to-cosmic-cyan/5", title: "Buy 5, Get 1 Half Off", text: "Every 5th container across your orders lands at half price — tracked automatically." },
  { icon: Gift, color: "text-cosmic-pink", bg: "from-cosmic-pink/20 to-cosmic-pink/5", title: "Buy 10, Get 1 Free", text: "Hit 10 containers and one is completely on us. The cosmos rewards loyalty." },
  { icon: Percent, color: "text-cosmic-yellow", bg: "from-cosmic-yellow/20 to-cosmic-yellow/5", title: "10% Off Your First Order", text: "Join the club and instantly get a personal code for 10% off your first container order." },
];

export const Loyalty = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/members", form);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="loyalty" data-testid="loyalty-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-cosmic-green">Cosmic Club</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            Loyalty that <span className="text-gradient-candy">rewards you back</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Join the Cosmic Club free. Every container you buy counts toward rewards — no card to carry,
            just use the same email at checkout.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 mb-12" data-testid="loyalty-perks">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass rounded-2xl p-6"
              data-testid={`loyalty-perk-${i}`}>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${p.bg} border border-white/10`}>
                <p.icon size={20} className={p.color} />
              </div>
              <h3 className="font-display mt-4 text-lg font-bold text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="glass relative overflow-hidden rounded-3xl p-8 sm:p-10 max-w-3xl">
          <div className="absolute -top-20 -right-20 h-52 w-52 rounded-full bg-cosmic-green/15 blur-3xl" />
          {result ? (
            <div className="relative text-center py-4" data-testid="loyalty-success">
              <CheckCircle2 size={48} className="mx-auto text-cosmic-green" />
              <h3 className="font-display mt-4 text-2xl font-black text-white">
                {result.existing ? "You're already in the club!" : "Welcome to the Cosmic Club!"}
              </h3>
              <p className="mt-2 text-sm text-slate-300">Your personal 10% off code (first container order):</p>
              <div data-testid="loyalty-code" className="font-mono mx-auto mt-4 inline-block rounded-xl border border-cosmic-green/40 bg-cosmic-green/10 px-6 py-3 text-xl font-bold tracking-[0.15em] text-cosmic-green">
                {result.welcome_code}
              </div>
              <p className="mt-4 text-xs text-slate-400">
                We also emailed it to you. Enter it in the coupon box at checkout — codes don't apply to event bookings.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="relative" data-testid="loyalty-form">
              <h3 className="font-display text-2xl font-black text-white">Claim your 10% welcome code</h3>
              <p className="mt-1.5 text-sm text-slate-400">Free to join. Your email doubles as your loyalty tracker.</p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <input data-testid="loyalty-name-input" required placeholder="Name" value={form.name} onChange={set("name")}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-green/60 focus:outline-none" />
                <input data-testid="loyalty-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-green/60 focus:outline-none" />
                <input data-testid="loyalty-phone-input" type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-green/60 focus:outline-none" />
              </div>
              <button data-testid="loyalty-signup-button" type="submit" disabled={loading}
                className="mt-5 rounded-full bg-gradient-to-r from-cosmic-green to-cosmic-cyan px-8 py-3.5 font-display text-sm font-bold text-cosmic-base transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50">
                {loading ? "Joining…" : "Join the Cosmic Club"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Loyalty;
